import { useCallback, useEffect, useRef, useState } from "react";

import { toast } from "@/shared/hooks/use-toast";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { logger } from "@/shared/lib/logger";

import {
  configIncompleteDetails,
  isPublishOutcomeUnknown,
  websiteService,
  type SiteContent,
  type SiteDetail,
  type Template,
} from "../../api/websiteService";
import { applyEdit, moveBlock as moveBlockIn, type EditRef } from "../../model/content";
import { previewHost } from "../../model/siteLabels";

/** 重写底稿上限(api `RewriteRequest.current` 的 `@Size(max = 2000)`,附录 A.36);超了 api 400,前端先拦。 */
export const REWRITE_CURRENT_MAX = 2000;

/**
 * 屏 4 的编辑器状态:本地可编辑副本 `content` + 脏标记 + 保存 / 发布 / AI 重写。
 *
 * ★ **一切改动都是函数式写**(`writeContent(prev => …)`,prev = `contentRef` 里最新那份):AI 重写、保存这些
 *   异步口回来时写在**最新**那份上,不是发起时的快照 —— 重写在路上时用户改了别的格,两边的改动都留着。
 *   每次改动 `revRef` 加一。
 * ★ 保存 = 整份 PUT(422 details 整批 toast)。保存在路上时用户接着改了,回来的那份**不盖**本地
 *   (否则刚敲的字丢了、还显示「已保存」):只有保存发起后没再改过,才换成 api 回的内容并清 dirty。
 * ★ 发布前若有未保存改动先保存再发(确认框里写明);发布成功 toast 带 previewUrl(stub 网关加「模拟发布」)
 *   并重拉站点让版本号走到下一位。
 * ★ 发布 409「结果未知」(发布服务器超时,版本留 pending,附录 A.38 / api `OUTCOME_UNKNOWN`):不画成红色「发布失败」,
 *   用中性 toast 说「结果未知,稍后看版本」并重拉站点;别的 409(失败 / 在途 / 被接替)照旧红色,也重拉一次站点 ——
 *   版本表已经变了(多了一行 failed / 别人的发布),页头的版本号别停在旧的。
 * ★ AI 重写送框里的当前值(附录 A.36 `current`),回值只进框(绑定标 `ai:rewrite`),保存草稿才落库;403 toast。
 * ★ 草稿 404(还没出过初稿)不当报错:`noDraft` 让页面画「还没有生成初稿」+ 回采集页的链接。
 * ★ 离开守卫:dirty 时挂 `beforeunload`(浏览器级离开);站内离开(返回 / 回采集页)调 `confirmLeave`。
 *   本仓是 `<BrowserRouter>`,没有 `useBlocker`,侧栏跳转拦不住(同 ProductTreePage 的说明)。
 * ★ 按站点 id 防串台:路由换站时组件不卸载,旧请求晚到的回包一律丢掉。
 */
export function useDraftEditor(id: string) {
  const confirm = useConfirm();
  const [site, setSite] = useState<SiteDetail | null>(null);
  const [template, setTemplate] = useState<Template | null>(null);
  const [content, setContent] = useState<SiteContent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [noDraft, setNoDraft] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const idRef = useRef(id);
  /** 本地改动的版本号:每次改动加一;保存据此判断「请求在路上时有没有人又改过」。 */
  const revRef = useRef(0);
  /** 最新提交的内容(保存 / 发布从这里拿,不吃闭包里的旧快照)。 */
  const contentRef = useRef<SiteContent | null>(null);

  /**
   * 写 content 的唯一口:`contentRef` 是最新那份的真身,**当场**算、当场写 ref,再交给 state 去渲染 ——
   * 改完紧接着(同一 tick、还没重渲染)调保存 / 发布,拿到的也是刚改的那份;连改两格,第二格基于第一格。
   * ★ 不能只在 `setContent(prev => …)` 的 updater 里写 ref:React 只在这个组件没有待处理更新时才当场跑
   *   updater,否则推到下次渲染 —— 那样同一 tick 里的保存还是读到旧的(useDraftEditor.test 实测过)。
   *   也不靠 effect 对齐(提交后才跑)。所有写 content 的地方都走这里,ref 与 state 不会分叉。
   */
  const writeContent = useCallback(
    (next: SiteContent | null | ((prev: SiteContent | null) => SiteContent | null)) => {
      const value = typeof next === "function" ? next(contentRef.current) : next;
      contentRef.current = value;
      setContent(value);
    },
    []
  );

  const alive = useCallback((forId: string) => idRef.current === forId, []);

  const load = useCallback(async () => {
    setError(null);
    setNoDraft(false);
    try {
      const s = await websiteService.getSite(id);
      if (!alive(id)) return;
      setSite(s);
      const [t, c] = await Promise.all([
        websiteService.getTemplate(s.templateCode),
        websiteService.getContent(id).catch((e: unknown) => {
          if ((e as { httpStatus?: unknown } | null)?.httpStatus === 404) return null;
          throw e;
        }),
      ]);
      if (!alive(id)) return;
      setTemplate(t);
      revRef.current += 1;
      writeContent(c);
      setNoDraft(c === null);
      setDirty(false);
    } catch (e) {
      if (alive(id)) setError(e instanceof Error ? e.message : String(e));
    }
  }, [id, alive, writeContent]);

  useEffect(() => {
    if (idRef.current !== id) {
      // 换站:上一站的本地状态作废(没保存的改动随站点一起丢 —— 站内离开已经问过)。
      setSite(null);
      setTemplate(null);
      writeContent(null);
      setDirty(false);
      setSaving(false);
      setPublishing(false);
    }
    idRef.current = id;
    void load();
  }, [id, load, writeContent]);

  // 浏览器级离开(关标签 / 刷新 / 前进后退):有未保存改动时弹原生确认(照 VideoCreatePage 的判例)。
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  /** 函数式改一份内容(永远基于最新那份);`fn` 必须是纯函数。 */
  const edit = useCallback(
    (fn: (prev: SiteContent) => SiteContent) => {
      revRef.current += 1;
      writeContent((prev) => (prev ? fn(prev) : prev));
      setDirty(true);
    },
    [writeContent]
  );

  /** 写一格:`path` 是完整绑定路径;区块字段带上 `blockId`(同页同型区块不止一块时认准那一块)。 */
  const applyAt = useCallback(
    (path: string, value: unknown, sourceRef: EditRef, blockId?: string) =>
      edit((prev) => applyEdit(prev, path, value, sourceRef, blockId)),
    [edit]
  );

  const moveBlock = useCallback(
    (slug: string, blockId: string, dir: -1 | 1) => {
      const current = contentRef.current;
      // 越界 / 找不到时不置脏(按钮本来就禁用,这里再挡一道)。
      if (!current || moveBlockIn(current, slug, blockId, dir) === current) return;
      edit((prev) => moveBlockIn(prev, slug, blockId, dir));
    },
    [edit]
  );

  /** 整份保存最新内容;回 true = 落库成功。 */
  const persist = useCallback(async (): Promise<boolean> => {
    const current = contentRef.current;
    if (!current) return false;
    const forId = id;
    setSaving(true);
    const startedAt = revRef.current;
    try {
      const saved = await websiteService.saveContent(forId, current);
      if (!alive(forId)) return false;
      // 保存期间又改过:本地那份更新,留着它、保持「未保存」,等下一次保存把它带上去。
      if (revRef.current === startedAt) {
        writeContent(saved);
        setDirty(false);
      }
      return true;
    } catch (e) {
      if (!alive(forId)) return false;
      const details = configIncompleteDetails(e);
      const message = e instanceof Error ? e.message : String(e);
      toast({
        title: details ? "草稿没通过校验" : "保存失败",
        description: details?.length ? details.join("\n") : message,
        variant: "destructive",
      });
      return false;
    } finally {
      if (alive(forId)) setSaving(false);
    }
  }, [id, alive, writeContent]);

  /**
   * AI 重写一格:送 `{path | token, instruction, current}`(附录 A.36 / A.55,host 模式按 token);
   * 成功回新值,失败已 toast、回 null。
   */
  const rewriteBy = useCallback(
    async (
      target: { path: string } | { token: string },
      instruction: string,
      current: string
    ): Promise<string | null> => {
      if (current.length > REWRITE_CURRENT_MAX) {
        toast({
          title: "这一格太长,AI 重写不了",
          description: `AI 重写最多处理 ${REWRITE_CURRENT_MAX} 字,先删减一些再试。`,
          variant: "destructive",
        });
        return null;
      }
      try {
        const { value } = await websiteService.rewrite(id, { ...target, instruction, current });
        return alive(id) ? value : null;
      } catch (e) {
        if (!alive(id)) return null;
        const status = (e as { httpStatus?: unknown } | null)?.httpStatus;
        toast({
          title: status === 403 ? "这一格 AI 不能改" : "AI 重写失败",
          description: e instanceof Error ? e.message : String(e),
          variant: "destructive",
        });
        return null;
      }
    },
    [id, alive]
  );

  const rewrite = useCallback(
    (path: string, instruction: string, current: string) =>
      rewriteBy({ path }, instruction, current),
    [rewriteBy]
  );

  /** host 模式(A.55):按宿主 token 重写一个移植节点。 */
  const rewriteToken = useCallback(
    (token: string, instruction: string, current: string) =>
      rewriteBy({ token }, instruction, current),
    [rewriteBy]
  );

  /** 发布之后(成功或 409)重拉站点:版本号 / 发布状态以 api 为准;失败只记日志。 */
  const refreshSite = useCallback(
    async (forId: string) => {
      try {
        const fresh = await websiteService.getSite(forId);
        if (alive(forId)) setSite(fresh);
      } catch (e) {
        logger.warn("[website] 发布后重拉站点失败", { error: String(e) });
      }
    },
    [alive]
  );

  const publish = useCallback(async () => {
    if (!site || !contentRef.current) return;
    const next = (site.currentVersionNo ?? 0) + 1;
    const ok = await confirm({
      title: `发布 v${next}?`,
      description: `${dirty ? "会先保存当前改动,再" : ""}以当前草稿生成 v${next} 并发布到 ${previewHost(site.previewUrl)}。`,
      confirmText: "发布",
    });
    if (!ok) return;
    const forId = id;
    setPublishing(true);
    try {
      if (dirty && !(await persist())) return;
      const v = await websiteService.publish(forId);
      if (!alive(forId)) return;
      toast({
        title: `已发布 v${v.versionNo}${v.publisherMode === "stub" ? " · 模拟发布" : ""}`,
        description: v.previewUrl ?? site.previewUrl,
      });
      await refreshSite(forId);
    } catch (e) {
      if (!alive(forId)) return;
      const message = e instanceof Error ? e.message : String(e);
      if (isPublishOutcomeUnknown(e)) {
        toast({ title: "发布结果未知,稍后看版本", description: message });
        await refreshSite(forId);
        return;
      }
      const details = configIncompleteDetails(e);
      toast({
        title: details ? "发布前校验没过" : "发布失败",
        description: details?.length ? details.join("\n") : message,
        variant: "destructive",
      });
      if ((e as { httpStatus?: unknown } | null)?.httpStatus === 409) await refreshSite(forId);
    } finally {
      if (alive(forId)) setPublishing(false);
    }
  }, [id, site, dirty, confirm, persist, alive, refreshSite]);

  /**
   * 站内离开前问一句;回 true = 可以走。没改动直接放行。
   * ★ 确认框只有「确认 / 取消」两个出口,三选一拆成两步:先问「保存并离开 / 不保存」,选不保存(或按 Esc)
   *   再问一次「直接离开 / 取消」—— Esc 不会把改动悄悄丢掉。保存失败就留在页面上(toast 已说原因)。
   */
  const confirmLeave = useCallback(async (): Promise<boolean> => {
    if (!dirty) return true;
    const save = await confirm({
      title: "有未保存的修改,离开会丢失。要先保存吗?",
      confirmText: "保存并离开",
      cancelText: "不保存",
      variant: "warning",
    });
    if (save) return persist();
    return confirm({
      title: "不保存,直接离开?",
      description: "这次的修改会丢失。",
      confirmText: "直接离开",
      cancelText: "取消",
      variant: "destructive",
    });
  }, [dirty, confirm, persist]);

  return {
    site,
    template,
    content,
    error,
    noDraft,
    dirty,
    saving,
    publishing,
    load,
    edit,
    applyAt,
    moveBlock,
    persist,
    publish,
    rewrite,
    rewriteToken,
    refreshSite,
    confirmLeave,
  };
}

export type DraftEditor = ReturnType<typeof useDraftEditor>;
